import {
  readFile as fsReadFile,
  writeFile as fsWriteFile,
  mkdir as fsMkdir,
  rm as fsRm,
  stat as fsStat,
  readdir as fsReaddir,
} from 'fs/promises';
import { join } from 'path';

/**
 * Virtual File System (VFS) helpers
 *
 * These utilities operate on a "file root" that can be either:
 * - a real filesystem directory path (string), or
 * - an in-memory object mapping normalized relative paths to content.
 *
 * Design notes:
 * - All relative paths are normalized with forward slashes and without leading separators.
 * - Text/binary reads are symmetric: strings are UTF-8 encoded to bytes for binary reads,
 *   and Uint8Array is UTF-8 decoded to string for text reads.
 * - Read APIs return a discriminated result with { ok: false, code: "ENOENT" | "READ_ERROR" } on failure.
 * - ".." segments that escape the root are rejected by normalization; display helpers keep raw when invalid.
 */
/** In-memory directory object mapping normalized relative paths to content */
export type InMemoryDir = Record<string, FileContent>;
/** File root: real filesystem directory path or in-memory object */
export type FileRoot = string | InMemoryDir;
/** File content as UTF-8 string or binary bytes */
export type FileContent = string | Uint8Array;

/**
 * Normalize a relative path for VFS operations.
 * - Removes leading separators
 * - Collapses repeated separators and dot segments
 * - Uses forward slashes
 * - Throws if traversal would escape the root (leading to an empty stack)
 */
export function normalizeRelPath(relPath: string): string {
  const trimmed = relPath.replace(/^[\\/]+/, '');
  const raw = trimmed.split(/[\\/]+/);
  const parts: string[] = [];

  for (const part of raw) {
    if (!part || part === '.') {
      continue;
    }

    if (part === '..') {
      if (parts.length === 0) {
        throw new Error('Path traversal outside root is not allowed');
      }

      parts.pop();
    } else {
      parts.push(part);
    }
  }

  return parts.join('/');
}

export function isInMemoryFileRoot(root: FileRoot): root is InMemoryDir {
  return typeof root === 'object' && root !== null;
}

/** Ensure the directory exists when using a real filesystem root. No-op for in-memory roots. */
export async function vfsEnsureDir(root: FileRoot): Promise<void> {
  if (!isInMemoryFileRoot(root)) {
    await fsMkdir(root, { recursive: true });
  }
}

/**
 * Write a file under the provided root at the normalized relative path.
 * - In-memory: stores the provided content as-is (string or Uint8Array)
 * - Filesystem: creates parent directories and writes the file
 */
export async function vfsWrite(
  root: FileRoot,
  relPath: string,
  content: FileContent,
): Promise<void> {
  const norm = normalizeRelPath(relPath);

  if (isInMemoryFileRoot(root)) {
    root[norm] = content;
    return;
  }

  const abs = join(root, norm);
  await fsMkdir(join(abs, '..'), { recursive: true }).catch(() => {});
  await fsWriteFile(abs, content);
}

/** Unified internal reader used by text/binary readers */
async function vfsReadRaw(
  root: FileRoot,
  relPath: string,
  desired: 'text' | 'Uint8Array',
): Promise<
  | { ok: true; data: string | Uint8Array }
  | { ok: false; code: 'ENOENT' | 'READ_ERROR'; message?: string }
> {
  try {
    if (isInMemoryFileRoot(root)) {
      const norm = normalizeRelPath(relPath);
      const data = root[norm];

      if (data === undefined) {
        return { ok: false, code: 'ENOENT' };
      }

      if (desired === 'Uint8Array') {
        if (data instanceof Uint8Array) {
          return { ok: true, data };
        }

        return { ok: true, data: new TextEncoder().encode(String(data)) };
      }

      // desired text
      if (typeof data === 'string') {
        return { ok: true, data };
      }

      return { ok: true, data: new TextDecoder().decode(data) };
    }

    const norm = normalizeRelPath(relPath);
    const abs = join(root, norm);
    const buf = await fsReadFile(abs);

    if (desired === 'Uint8Array') {
      return { ok: true, data: buf };
    }

    return { ok: true, data: buf.toString('utf8') };
  } catch (error) {
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'ENOENT'
    ) {
      return { ok: false, code: 'ENOENT' };
    }

    return {
      ok: false,
      code: 'READ_ERROR',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

/** Read a file as UTF-8 text with consistent result shape across roots. */
export async function vfsReadText(
  root: FileRoot,
  relPath: string,
): Promise<
  | { ok: true; text: string }
  | { ok: false; code: 'ENOENT' | 'READ_ERROR'; message?: string }
> {
  const res = await vfsReadRaw(root, relPath, 'text');

  if (!res.ok) {
    return res;
  }

  return { ok: true, text: res.data as string };
}

/** Read a file as raw bytes (Uint8Array) with consistent result shape across roots. */
export async function vfsReadBinary(
  root: FileRoot,
  relPath: string,
): Promise<
  | { ok: true; data: Uint8Array }
  | { ok: false; code: 'ENOENT' | 'READ_ERROR'; message?: string }
> {
  const res = await vfsReadRaw(root, relPath, 'Uint8Array');

  if (!res.ok) {
    return res;
  }

  return { ok: true, data: res.data as Uint8Array };
}

/**
 * Delete a file at the normalized relative path.
 * Returns true if the file was deleted, false if it didn't exist.
 * NOTE: This function is designed for files only. It does not support deleting directories (recursive or otherwise).
 */
export async function vfsDeleteFile(
  root: FileRoot,
  relPath: string,
): Promise<boolean> {
  const norm = normalizeRelPath(relPath);

  if (isInMemoryFileRoot(root)) {
    if (root[norm] !== undefined) {
      delete root[norm];
      return true;
    }

    return false;
  }

  const abs = join(root, norm);
  try {
    await fsRm(abs);
    return true;
  } catch (error) {
    // If file doesn't exist, return false
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'ENOENT'
    ) {
      return false;
    }

    // Other errors (permissions, etc.) should propagate
    throw error;
  }
}

/**
 * Check if a path exists in the file root (file or directory).
 * This function checks for the existence of any path, whether it's a file or directory.
 * @param root - File root (filesystem path or in-memory object)
 * @param relPath - Relative path to check
 * @returns true if the path exists (file or directory), false otherwise
 */
export async function vfsExists(
  root: FileRoot,
  relPath: string,
): Promise<boolean> {
  const norm = normalizeRelPath(relPath);

  if (isInMemoryFileRoot(root)) {
    return root[norm] !== undefined;
  }

  // For filesystem, use stat to check if path exists
  const abs = join(root, norm);

  try {
    await fsStat(abs);
    return true;
  } catch (error) {
    // Path doesn't exist
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'ENOENT'
    ) {
      return false;
    }

    // Other errors (permissions, etc.) should propagate
    throw error;
  }
}

/**
 * Write a file only if it doesn't already exist.
 * Returns true if the file was written, false if it already existed.
 * @param root - File root (filesystem path or in-memory object)
 * @param relPath - Relative path to the file
 * @param content - Content to write (string or Uint8Array)
 * @throws {Error} If filesystem operation fails (e.g., permission denied, read-only filesystem)
 */
export async function vfsWriteIfNotExists(
  root: FileRoot,
  relPath: string,
  content: FileContent,
): Promise<boolean> {
  const norm = normalizeRelPath(relPath);

  if (isInMemoryFileRoot(root)) {
    if (root[norm] !== undefined) {
      return false;
    }

    root[norm] = content;
    return true;
  }

  // For filesystem, use stat to check if file exists (more efficient than reading)
  const abs = join(root, norm);

  try {
    await fsStat(abs);
    // File exists, don't overwrite
    return false;
  } catch (error) {
    // Only proceed if file doesn't exist (ENOENT)
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'ENOENT'
    ) {
      await vfsWrite(root, relPath, content);
      return true;
    }

    // Other errors (permissions, read-only filesystem, etc.) should propagate
    throw error;
  }
}

/**
 * Write JSON data to a file with optional human-readable formatting.
 * @param root - File root (filesystem path or in-memory object)
 * @param relPath - Relative path to the JSON file
 * @param data - Data to serialize as JSON
 * @param useHumanFormat - Whether to format with indentation (default: true)
 */
export async function vfsWriteJSON(
  root: FileRoot,
  relPath: string,
  data: unknown,
  useHumanFormat = true,
): Promise<void> {
  const jsonString = useHumanFormat
    ? JSON.stringify(data, null, 2)
    : JSON.stringify(data);

  await vfsWrite(root, relPath, jsonString);
}

/**
 * Read and parse JSON data from a file.
 * Returns a discriminated result with parse error handling.
 */
export async function vfsReadJSON<T = unknown>(
  root: FileRoot,
  relPath: string,
): Promise<
  | { ok: true; data: T }
  | {
      ok: false;
      code: 'ENOENT' | 'READ_ERROR' | 'PARSE_ERROR';
      message?: string;
    }
> {
  const textResult = await vfsReadText(root, relPath);

  if (!textResult.ok) {
    return textResult;
  }

  try {
    const data = JSON.parse(textResult.text) as T;

    return { ok: true, data };
  } catch (parseError) {
    return {
      ok: false,
      code: 'PARSE_ERROR',
      message:
        parseError instanceof Error ? parseError.message : 'Invalid JSON',
    };
  }
}

/**
 * List directory contents (files and subdirectories) at the specified path.
 * Returns an array of entry names (not full paths).
 * @param root - File root (filesystem path or in-memory object)
 * @param relPath - Relative path to list (default: root)
 * @param excludes - Array of filenames to exclude from the result
 * @returns Array of entry names in the directory
 */
export async function vfsListDir(
  root: FileRoot,
  relPath = '',
  excludes: string[] = [],
): Promise<string[]> {
  let entries: string[] = [];

  if (isInMemoryFileRoot(root)) {
    // For in-memory roots, we simulate directory listing by checking path prefixes
    // keys are normalized relative paths like "a/b/c.txt"
    const norm = normalizeRelPath(relPath);
    const prefix = norm ? norm + '/' : '';
    const entrySet = new Set<string>();

    for (const path of Object.keys(root)) {
      // Check if this file is within the requested directory
      if (path.startsWith(prefix) && path !== norm) {
        // We found a file inside the target directory (possibly deep inside)
        // "sub" is the path relative to the target directory
        const sub = path.slice(prefix.length);
        const firstSlash = sub.indexOf('/');

        if (firstSlash === -1) {
          // No slashes means it's a direct child file
          entrySet.add(sub);
        } else {
          // Slashes mean it's in a subdirectory, so we add the subdirectory name
          entrySet.add(sub.substring(0, firstSlash));
        }
      }
    }

    entries = Array.from(entrySet).sort();
  } else {
    // For filesystem, use standard readdir
    try {
      const norm = normalizeRelPath(relPath);
      const abs = join(root, norm);
      const fsEntries = await fsReaddir(abs);
      entries = fsEntries.sort();
    } catch (error) {
      // If directory doesn't exist or can't be read, return empty array
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        (error as { code?: unknown }).code === 'ENOENT'
      ) {
        return [];
      }

      // Other errors should propagate
      throw error;
    }
  }

  if (excludes.length > 0) {
    return entries.filter((e) => !excludes.includes(e));
  }

  return entries;
}

/**
 * Display helper for logging/debugging paths in a consistent format across roots.
 * - Memory roots: "[in-memory]" optionally followed by normalized (or raw if invalid) path
 * - Filesystem roots: absolute path via join(root, normalizedRelPath) or join(root, rawRelPath) if invalid
 */
export function vfsDisplayPath(root: FileRoot, relPath?: string): string {
  if (isInMemoryFileRoot(root)) {
    if (!relPath) {
      return `[in-memory]`;
    }

    let norm = relPath;

    try {
      norm = normalizeRelPath(relPath);
    } catch {
      // keep raw
    }

    return `[in-memory] ${norm}`;
  }

  if (!relPath) {
    return root;
  }

  let norm = relPath;

  try {
    norm = normalizeRelPath(relPath);
  } catch {
    // keep raw
  }

  return join(root, norm);
}
